import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { account, wordFor } from "../account.ts";
import type { SignedIn } from "../account.ts";
import { AuthPage, messageOf } from "./Login.tsx";
import { IS_PANTRY } from "../brand.ts";

/** A new workspace with its owner; fields and rules as in the sister apps (docs/14-anmeldung.md). */
export function Signup({ onSignedIn }: { onSignedIn: (s: SignedIn) => void }) {
  const [open, setOpen] = useState<boolean | null>(null);
  const [kind, setKind] = useState<"business" | "private">(IS_PANTRY ? "private" : "business");
  const [form, setForm] = useState({ workspace: "", displayName: "", email: "", username: "", password: "", repeat: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const word = wordFor(kind);

  useEffect(() => {
    account.signupOpen().then(setOpen, () => setOpen(false));
  }, []);

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value }),
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (form.password.length < 10) return setError("Das Passwort braucht mindestens 10 Zeichen.");
    if (form.password !== form.repeat) return setError("Die beiden Passwörter sind nicht gleich.");
    setBusy(true);
    try {
      const { repeat: _repeat, ...body } = form;
      onSignedIn(await account.signup({ ...body, username: body.username.trim(), kind }));
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  if (open === false) {
    return (
      <AuthPage>
        <div className="card">
          <h2>Neu anlegen</h2>
          <p>{IS_PANTRY ? "Neue Haushalte legt hier gerade nur der Betreiber an. Gehörst du zu einem Haushalt, der die App schon nutzt, bekommst du eine Einladung." : "Neue Betriebe legt hier gerade nur der Betreiber an. Gehörst du zu einem Betrieb, der die App schon nutzt, bekommst du vom Inhaber eine Einladung."}</p>
          <a className="button" href="#/anmelden">
            Zur Anmeldung
          </a>
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <div className="card">
        <h2>Neu anlegen</h2>
        <p className="muted">Du legst {word.the} an und bist sein Inhaber. Was ihr eintragt, sieht nur, wer dazugehört.</p>
        <form noValidate onSubmit={submit}>
          {/* VorratsBuch is for households; a business with the food profile comes later (B2). */}
          {!IS_PANTRY && (
            <div className="choice" role="radiogroup" aria-label="Art">
              {(["business", "private"] as const).map((k) => (
                <label key={k} className={kind === k ? "on" : ""}>
                  <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} />
                  {k === "business" ? "Betrieb (Gastronomie)" : "Keller (privat)"}
                </label>
              ))}
            </div>
          )}
          <label>
            Name {word.of}
            <input {...field("workspace")} maxLength={60} autoComplete="off" placeholder={kind === "business" ? "z. B. Weinstube am Markt" : IS_PANTRY ? "z. B. Haushalt Weber" : "z. B. Keller Weber"} data-testid="workspace" />
          </label>
          <label>
            Dein Name
            <input {...field("displayName")} maxLength={40} autoComplete="nickname" data-testid="display-name" />
            <span className="hint" data-testid="display-name-hint">So nennen dich die anderen in der App, am besten mit dem Vornamen.</span>
          </label>
          <label>
            E-Mail-Adresse
            <input {...field("email")} type="email" maxLength={200} autoComplete="email" data-testid="email" />
            <span className="hint">Damit du dein Passwort selbst zurücksetzen kannst. Nie für Werbung. Später jederzeit änderbar.</span>
          </label>
          <label>
            Benutzername
            <input {...field("username")} maxLength={64} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="new-username" />
            <span className="hint">Zum Anmelden. Darf auch deine E-Mail-Adresse sein.</span>
          </label>
          <label>
            Passwort
            <input {...field("password")} type="password" autoComplete="new-password" data-testid="new-password" />
            <span className="hint">Mindestens 10 Zeichen.</span>
          </label>
          <label>
            Passwort wiederholen
            <input {...field("repeat")} type="password" autoComplete="new-password" data-testid="repeat-password" />
          </label>
          {error && <p className="error-box">{error}</p>}
          <button className="primary" disabled={busy || open === null} data-testid="signup">
            {busy ? `${word.one} anlegen …` : `${word.one} anlegen`}
          </button>
        </form>
        <p className="center">
          <a href="#/anmelden">Ich habe schon ein Konto</a>
        </p>
      </div>
    </AuthPage>
  );
}
